<template>
  <div>
    <main-layout
      controlsBg="light"
      :hasControlsBar="false"
      :breadcrumb="getBreadcrumb">
      <div class="container">
        <grid-row class="margin-top-xl" column-align="center">
          <grid-col col-size="4">
            <div id="header">
              <h1 class="header-title">
                <chevron
                  @click="$router.push({name: 'Settings'})"/>
                {{ $t('health.corporateGroups.title') }}
              </h1>
              <div class="header-add-button">
                <span
                  v-on:click="showAddModal = true"
                  v-tooltip.left.top-start="$t('group.add')">
                  <font-awesome-icon
                    class="header-add-button-icon"
                    :icon="icon.add"/>
                </span>
              </div>
            </div>
            <alert
              v-if="alertVisible"
              :message="alertMessage"
              :type="alertType">
            </alert>
          </grid-col>
        </grid-row>
        <grid-row class="margin-top-xl" column-align="center">
          <grid-col col-size="4">
            <table-component
              ref="groupTable"
              :data="fetchData"
              sort-by="name"
              sort-order="asc"
              :show-caption="false"
              class="datatable"
              filter-placeholder=""
              filterNoResults="">
              <table-column show="id" label="Id" :hidden="true"></table-column>
              <table-column
                show="name"
                :label="$t('health.corporateGroups.name')"
                header-class="text-left"
                cell-class="table-component__text-left">
              </table-column>
              <table-column
                show="domain"
                :label="$t('health.corporateGroups.domain')"
                header-class="text-left"
                cell-class="table-component__text-left">
              </table-column>
              <table-column
                :label="$t('health.corporateGroups.activePlan')"
                :sortable="false"
                :filterable="false"
                header-class="table-component__minimum__width__column"
                cell-class="text-center">
                <template slot-scope="row">
                  <font-awesome-icon
                    v-if="row.activePlan === true"
                    :icon="icon.check"
                    class="table-iconset-icon"/>
                </template>
              </table-column>
              <table-column
                label=""
                :sortable="false"
                :filterable="false"
                header-class="table-component__minimum__width__column"
                cell-class="text-center">
                <template slot-scope="row">
                  <font-awesome-icon
                    :id="row"
                    v-on:click="onUpdate(row)"
                    :icon="icon.pencil"
                    class="table-iconset-icon pointer"/>
                </template>
              </table-column>
              <table-column
                label=""
                :sortable="false"
                :filterable="false"
                header-class="table-component__minimum__width__column"
                cell-class="text-center">
                <template slot-scope="row">
                  <font-awesome-icon
                    :id="row"
                    v-on:click="onDelete(row)"
                    :icon="icon.trash"
                    class="table-iconset-icon pointer"/>
                </template>
              </table-column>
            </table-component>
          </grid-col>
        </grid-row>
      </div>
      <add-corporate-group-modal
        v-if="showAddModal"
        :groupOptions="getGroupOptions"
        v-on:input="saveGroupInput"
        v-on:close="showAddModal = false"
      />
      <edit-corporate-group-modal
        v-if="showEditModal"
        v-model="groupToEdit"
        v-on:input="saveGroupInput"
        v-on:close="showEditModal = false"
      />
      <ok-cancel-modal
        v-if="showConfirmationDeleteModal"
        v-on:ok="onDeleteConfirm"
        v-on:cancel="onDeleteCancel"
        :title="deleteModalMessage"
        :message="deleteModalMessage"
        :okButton="$t('commons.delete')"
        :cancelButton="$t('commons.cancel')">
        <div slot="body">
          <ul>
            <li>
              {{ groupToDelete.name }}
            </li>
          </ul>
        </div>
      </ok-cancel-modal>
    </main-layout>
  </div>
</template>

<script>
import MainLayout from '@/layouts/MainLayout';
import GridRow from '@/components/shared/grid/GridRow';
import GridCol from '@/components/shared/grid/GridCol';
import pagination from '@/components/shared/table/pagination';
import api from 'BRAND/api/corporateGroups';
import FontAwesomeIcon from '@fortawesome/vue-fontawesome';
import {faCheck, faKey, faPencilAlt, faTrashAlt, faUsers} from '@fortawesome/pro-regular-svg-icons';
import {faPlusCircle} from '@fortawesome/pro-solid-svg-icons';
import TableComponent from '@/components/shared/table/components/TableComponent';
import TableColumn from '@/components/shared/table/components/TableColumn';
import Alert from '@/components/shared/alert/Alert';
import OkCancelModal from '@/components/shared/dialogs/OkCancelModal';
import BtnPill from '@/components/shared/buttons/BtnPill';
import Chevron from '@/components/shared/Chevron';
import EditCorporateGroupModal from 'BRAND/layouts/corporateGroups/EditCorporateGroupModal';
import AddCorporateGroupModal from 'BRAND/layouts/corporateGroups/AddCorporateGroupModal';
import {mapActions, mapGetters} from 'vuex';

export default {
  name: 'CorporativeGroups',
  components: {
    AddCorporateGroupModal,
    EditCorporateGroupModal,
    MainLayout,
    GridRow,
    GridCol,
    TableComponent,
    TableColumn,
    FontAwesomeIcon,
    OkCancelModal,
    Alert,
    BtnPill,
    Chevron
  },
  data () {
    return {
      groups: [],
      alertVisible: false,
      alertMessage: '',
      alertType: '',
      groupToDelete: undefined,
      groupToEdit: undefined,
      showAddModal: false,
      showEditModal: false,
      showConfirmationDeleteModal: false,
      deleteModalMessage: '',
      icon: {
        add: faPlusCircle,
        trash: faTrashAlt,
        pencil: faPencilAlt,
        users: faUsers,
        check: faCheck,
        key: faKey
      },
      tablePageSize: 10,
      tableState: {
        filter: '',
        page: 1,
        sort: {
          order: '',
          fieldName: ''
        }
      }
    };
  },
  watch: {
    getExistingGroups: {
      handler: function () {
        this.$refs.groupTable.refresh();
      }
    }
  },
  created () {
    this.retrieveGroups({context: this});
    this.requestData();

    const alert = this.$route.params.alert;
    if (alert !== undefined) {
      showAlert(this, this.$t(alert), 'success');
    }
  },
  computed: {
    ...mapGetters([
      'getGroups'
    ]),
    getGroupOptions () {
      const excludedGroupIds = this.groups.map(group => group.groupId);
      const groups = this.getGroups
        .filter(group => !excludedGroupIds.includes(group.id))
        // Root group can't be added to another group
        .filter(group => group.parentId !== null)
        .map(group => {
          return {
            value: group.id,
            label: group.name
          };
        });
      return groups;
    },
    getBreadcrumb () {
      return [
        {
          link: '/settings',
          name: this.$t('menu.settings')
        },
        {
          link: '/corporate-groups',
          name: this.$t('health.corporateGroups.title'),
          disabled: true
        }
      ];
    },
    getExistingGroups () {
      return this.groups;
    }
  },
  methods: {
    requestData () {
      api.getGroups(this).then((response) => {
        this.groups = response;
      }).catch(() => {
        showAlert(this, this.$t('health.corpGroups.errorGettingGroups'), 'error');
      });
    },
    fetchData ({page, filter, sort}) {
      const tablePage = pagination.createPage(
        this.getExistingGroups,
        this.tableState,
        ['name'],
        this.tablePageSize,
        {page, filter, sort});
      this.tableState = tablePage.state;
      return tablePage.result;
    },
    onDelete (group) {
      this.groupToDelete = group;
      this.deleteModalMessage = this.$t('group.deleteModalMessage');
      this.showConfirmationDeleteModal = true;
    },
    onDeleteConfirm () {
      const id = this.groupToDelete.id;
      const groupName = this.groupToDelete.name;
      this.groupToDelete = {};
      this.showConfirmationDeleteModal = false;
      api.deleteGroup(this, id).then(() => {
        showAlert(this, this.$t('group.successDelete', {name: groupName}), 'success');
        this.requestData();
      }, () => {
        showAlert(this, this.$t('group.errorDelete', {name: groupName}), 'error');
      });
    },
    onDeleteCancel () {
      this.groupToDelete = {};
      this.showConfirmationDeleteModal = false;
    },
    onUpdate (group) {
      this.groupToEdit = group;
      this.showEditModal = true;
    },
    saveGroupInput (group) {
      const id = group.id;
      let request;
      if (id == null || id === '') {
        request = api.createGroup(this, group);
      } else {
        request = api.updateGroup(this, id, group);
      }
      request.then(() => {
        this.showEditModal = false;
        this.showAddModal = false;
        this.groupToEdit = {};
        showAlert(this, this.$t('group.successUpdate', {name: group.name}), 'success');
        this.requestData();
      }, (error) => {
        this.showEditModal = false;
        this.showAddModal = false;
        this.groupToEdit = {};
        if (error.status === 409) {
          showAlert(this, this.$t('health.corporateGroups.groupWithDomainAlreadyExists', {name: group.name}), 'error');
        } else {
          showAlert(this, this.$t('group.errorUpdate', {name: group.name}), 'error');
        }
      });
    },
    ...mapActions([
      'retrieveGroups'
    ])
  }
};

const showAlert = (context, message, type) => {
  context.alertVisible = true;
  context.alertMessage = message;
  context.alertType = type;
  context.showAlert = true;
  setTimeout(() => {
    context.alertVisible = false;
  }, 2000);
};
</script>

<style lang="scss">
@import "src/stylesheets/variables";

#header {
  display: inline-block;
  width: 100%;
  line-height: 1rem;
}

.header-title {
  float: left;
}

.header-add-button {
  float: right;
}

.header-add-button-icon {
  font-size: 3rem;
  color: $secondary;
}

.table-iconset-icon {
  margin: 0 0.5em;
}

.pointer {
  cursor: pointer;
}

.table-icon-disabled {
  opacity: 0.4;
  margin: 0 0.5em;
  cursor: default;
}

.table-component {
  $filter-padding: $sp-margin-smaller;

  &__th--sort,
  &__th--sort-asc,
  &__th--sort-desc {
    cursor: pointer;
    position: relative;
    padding-right: 1rem !important;

    &::before,
    &::after {
      position: absolute;
      font-size: 0.3rem;
      right: 0.3rem;
      opacity: 0.5;
    }

    &:before {
      top: 0.7rem;
    }

    &:after {
      bottom: 0.6rem;
    }
  }
}
</style>



// WEBPACK FOOTER //
// src/brand/health/layouts/corporateGroups/CorporateGroupsList.vue